import React from 'react';
import {
    Users, CheckCircle, Clock, Coins, TrendingUp, TrendingDown,
    Briefcase, BookOpen, Building2, GraduationCap, Plus, Edit2,
    DollarSign, Eye, Trash2, MapPin,
    ArrowUpRight, ArrowDownRight, Ban, CheckCircle2, XOctagon
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

// Helper to safely extract error message from API responses
// FastAPI 422 errors return detail as an array of objects, not a string
export const getApiErrorMessage = (error, fallback = 'Something went wrong') => {
    const detail = error?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail.map(d => typeof d === 'object' ? (d.msg || JSON.stringify(d)) : String(d)).join('; ');
    }
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return String(detail);
};

// Stats Card Component
export const StatsCard = ({ title, value, change, changeType, icon: Icon, color, subtext }) => (
    <Card className="relative overflow-hidden">
        <CardContent className="p-6">
            <div className="flex items-start justify-between">
                <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-500">{title}</p>
                    <p className="text-3xl font-bold text-gray-900">{value}</p>
                    {change !== undefined && (
                        <div className="flex items-center space-x-1">
                            {changeType === 'increase' ? (
                                <ArrowUpRight className="h-4 w-4 text-green-500" />
                            ) : (
                                <ArrowDownRight className="h-4 w-4 text-red-500" />
                            )}
                            <span className={`text-sm font-medium ${changeType === 'increase' ? 'text-green-600' : 'text-red-600'}`}>
                                {change}%
                            </span>
                            <span className="text-sm text-gray-500">vs last week</span>
                        </div>
                    )}
                    {subtext && <p className="text-xs text-gray-400">{subtext}</p>}
                </div>
                <div className={`p-3 rounded-xl ${color}`}>
                    <Icon className="h-6 w-6 text-white" />
                </div>
            </div>
        </CardContent>
        <div className={`absolute bottom-0 left-0 right-0 h-1 ${color}`} />
    </Card>
);

// Mini Chart Component (simple bar visualization)
export const MiniChart = ({ data, color }) => {
    const max = Math.max(...data, 1);
    return (
        <div className="flex items-end space-x-1 h-12">
            {data.map((value, index) => (
                <div
                    key={index}
                    className={`w-2 rounded-t ${color} opacity-${Math.max(30, Math.round((value / max) * 100))}`}
                    style={{ height: `${Math.max(10, (value / max) * 100)}%`, opacity: 0.3 + (value / max) * 0.7 }}
                />
            ))}
        </div>
    );
};

// User Row Component
export const UserRow = ({ user, onAddCoins, onSuspend, onDelete, onToggleVerification, onView, onEdit }) => (
    <tr className="border-b hover:bg-gray-50 transition-colors">
        <td className="px-4 py-4">
            <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${user.role === 'admin' ? 'bg-red-500' :
                    user.role === 'tutor' ? 'bg-blue-500' :
                        user.role === 'student' ? 'bg-green-500' : 'bg-purple-500'
                    }`}>
                    {user.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                    <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{user.email}</p>
                        {user.is_verified && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">Verified</Badge>
                        )}
                    </div>
                    <p className="text-sm text-gray-500">{user.phone || 'No phone'}</p>
                </div>
            </div>
        </td>
        <td className="px-4 py-4">
            <Badge className={`capitalize ${user.role === 'admin' ? 'bg-red-100 text-red-700' :
                user.role === 'tutor' ? 'bg-blue-100 text-blue-700' :
                    user.role === 'student' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                }`}>
                {user.role}
            </Badge>
        </td>
        <td className="px-4 py-4">
            <Badge className={user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                {user.is_active ? 'Active' : 'Suspended'}
            </Badge>
        </td>
        <td className="px-4 py-4">
            <div className="flex items-center space-x-1">
                <Coins className="h-4 w-4 text-yellow-500" />
                <span className="font-medium">{user.wallet_balance || 0}</span>
            </div>
        </td>
        <td className="px-4 py-4 text-sm text-gray-500">
            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
        </td>
        <td className="px-4 py-4">
            <div className="flex items-center space-x-2">
                <Button size="sm" variant="outline" onClick={() => onEdit(user, 'user')} title="Edit User">
                    <Edit2 className="h-4 w-4 text-purple-600" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => onToggleVerification(user)} title={user.is_verified ? "Unverify User" : "Verify User"}>
                    {user.is_verified ? <XOctagon className="h-4 w-4 text-orange-600" /> : <CheckCircle2 className="h-4 w-4 text-blue-600" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => onAddCoins(user)} title="Add Coins">
                    <Plus className="h-4 w-4" />
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onSuspend(user)}
                    className={user.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}
                    title={user.is_active ? 'Suspend' : 'Activate'}
                >
                    {user.is_active ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDelete(user)}
                    className="text-red-600 hover:bg-red-50"
                    title="Delete"
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            </div>
        </td>
    </tr>
);

// Content Item Card Component
export const ContentItemCard = ({ item, onReview, onSetPrice, onEdit, getItemIcon, getItemTitle }) => (
    <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-yellow-400">
        <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start space-x-4 flex-1">
                    <div className={`p-3 rounded-xl ${item.type === 'job' ? 'bg-blue-100' :
                        item.type === 'tuition' ? 'bg-green-100' :
                            item.type === 'tutor' ? 'bg-purple-100' : 'bg-orange-100'
                        }`}>
                        {getItemIcon(item.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900 truncate">{getItemTitle(item)}</h3>
                            <Badge variant="secondary" className="capitalize">{item.type}</Badge>
                            <Badge className="bg-yellow-100 text-yellow-700">
                                <Clock className="h-3 w-3 mr-1" />
                                Pending
                            </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                            {item.type === 'job' && (
                                <>
                                    <div className="flex items-center"><Building2 className="h-4 w-4 mr-1 text-gray-400" />{item.organization || 'N/A'}</div>
                                    <div className="flex items-center"><MapPin className="h-4 w-4 mr-1 text-gray-400" />{item.location || 'N/A'}</div>
                                    <div className="flex items-center"><DollarSign className="h-4 w-4 mr-1 text-gray-400" />{item.salary || 'N/A'}</div>
                                </>
                            )}
                            {item.type === 'tuition' && (
                                <>
                                    <div className="flex items-center"><BookOpen className="h-4 w-4 mr-1 text-gray-400" />{item.subjects?.join(', ') || 'N/A'}</div>
                                    <div className="flex items-center"><MapPin className="h-4 w-4 mr-1 text-gray-400" />{item.city || 'N/A'}</div>
                                    <div className="flex items-center"><DollarSign className="h-4 w-4 mr-1 text-gray-400" />₹{item.budget_amount}/{item.budget_type}</div>
                                </>
                            )}
                            {item.type === 'tutor' && (
                                <>
                                    <div className="flex items-center"><GraduationCap className="h-4 w-4 mr-1 text-gray-400" />{item.qualification || 'N/A'}</div>
                                    <div className="flex items-center"><Clock className="h-4 w-4 mr-1 text-gray-400" />{item.experience_years || 0} years exp</div>
                                    <div className="flex items-center"><DollarSign className="h-4 w-4 mr-1 text-gray-400" />₹{item.hourly_rate}/hr</div>
                                </>
                            )}
                            {item.type === 'organization' && (
                                <>
                                    <div className="flex items-center"><Building2 className="h-4 w-4 mr-1 text-gray-400" />{item.organization_type || 'N/A'}</div>
                                    <div className="flex items-center"><Users className="h-4 w-4 mr-1 text-gray-400" />{item.contact_person_name || 'N/A'}</div>
                                    <div className="flex items-center"><MapPin className="h-4 w-4 mr-1 text-gray-400" />{item.city || 'N/A'}</div>
                                </>
                            )}
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-full">
                                <Coins className="h-4 w-4 text-yellow-500" />
                                <span className="text-sm font-medium">{item.unlock_price || 0} coins</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col space-y-2">
                    <Button size="sm" onClick={() => onEdit(item, item.type)} className="bg-purple-600 hover:bg-purple-700">
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                    </Button>
                    <Button size="sm" onClick={() => onReview(item, item.type)} className="bg-blue-600 hover:bg-blue-700">
                        <Eye className="h-4 w-4 mr-2" />
                        Review
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onSetPrice(item)}>
                        <Coins className="h-4 w-4 mr-2" />
                        Set Price
                    </Button>
                </div>
            </div>
        </CardContent>
    </Card>
);
